
import { DiaryEntry } from "@/utils/diary";
import EntryCard from "@/components/EntryCard";
import { memo, useEffect } from "react";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { Skeleton } from "@/components/ui/skeleton";
import EntriesPagination from "./pagination/EntriesPagination";
import { useEntriesPagination } from "./hooks/useEntriesPagination";

interface EntryCardsListProps {
  entries: DiaryEntry[];
  onDelete: (id: string) => void;
  onUpdate: (updatedEntry: DiaryEntry) => void;
  isLoading?: boolean;
}

const EntryCardsList = memo(({ entries, onDelete, onUpdate, isLoading = false }: EntryCardsListProps) => {
  const { language } = useLanguage();
  
  // Use the custom pagination hook
  const { 
    paginatedEntries, 
    currentPage, 
    totalPages, 
    handlePageChange 
  } = useEntriesPagination(entries, "", undefined);
  
  // Track page views for better analytics
  useEffect(() => {
    if (typeof window !== 'undefined' && !isLoading && entries.length > 0) {
      // This helps search engines understand page transitions in SPAs
      const pageTitle = `Diary Entries - Page ${currentPage}`;
      const pageUrl = window.location.href.split('?')[0] + `?page=${currentPage}`;
      
      // Update browser history without reload for SEO crawlers
      window.history.replaceState(
        { page: currentPage },
        pageTitle,
        pageUrl
      );
    }
  }, [currentPage, entries.length, isLoading]);
  
  // Early render for skeleton loaders
  if (isLoading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={`skeleton-${index}`} className="border rounded-lg p-4">
            <div className="space-y-4">
              <div className="flex justify-between">
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-6 w-1/4" />
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  // Return null if no entries
  if (!entries.length) {
    return null;
  }
  
  return (
    <div className="space-y-6">
      <h2 className="sr-only">{t('entries.diaryEntryList', language) || 'Diary Entries'}</h2>
      <div 
        className="grid grid-cols-1 gap-6"
        aria-label={t('entries.diaryEntryList', language) || 'List of diary entries'}
      >
        {paginatedEntries.map((entry) => (
          <div 
            key={entry.id}
            className="transform transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
          >
            <EntryCard
              entry={entry}
              onDelete={() => onDelete(entry.id)}
              onUpdate={onUpdate}
            />
          </div>
        ))}
      </div>
      
      {totalPages > 1 && (
        <EntriesPagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
});

EntryCardsList.displayName = "EntryCardsList";

export default EntryCardsList;
